import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Subscription() {
  const [subscription, setSubscription] = useState(null);
  const [host, setHost] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('month');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSubscription();
    fetchTiers();
  }, []);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
        setHost(data.host);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setLoading(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/available-tiers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedTier) {
      alert('Please select a plan');
      return;
    }

    if (!window.confirm(`Are you sure you want to change to ${selectedTier.name}? You'll be charged/credited the prorated difference.`)) {
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/subscription/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tier_id: selectedTier.id,
          billing_cycle: selectedCycle
        })
      });

      const data = await response.json();

      if (response.ok) {
        alert('✅ Subscription updated successfully!');
        setShowUpgrade(false);
        fetchSubscription();
      } else {
        alert(`Error: ${data.error || 'Failed to update subscription'}`);
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async (immediately) => {
    const message = immediately
      ? 'Are you sure you want to cancel immediately? You will lose access right away.'
      : 'Are you sure? Your subscription will remain active until the end of your billing period.';

    if (!window.confirm(message)) {
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cancel_immediately: immediately })
      });

      const data = await response.json();

      if (response.ok) {
        alert('✅ ' + data.message);
        fetchSubscription();
      } else {
        alert(`Error: ${data.error || 'Failed to cancel subscription'}`);
      }
    } catch (error) {
      console.error('Cancel error:', error);
      alert('Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    if (!window.confirm('Reactivate your subscription?')) {
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/subscription/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        alert('✅ ' + data.message);
        fetchSubscription();
      } else {
        alert(`Error: ${data.error || 'Failed to reactivate subscription'}`);
      }
    } catch (error) {
      console.error('Reactivate error:', error);
      alert('Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdatePayment = async () => {
    setProcessing(true);

    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/subscription/portal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        alert(`Error: ${data.error || 'Failed to open payment portal'}`);
        setProcessing(false);
      }
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open payment portal');
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      past_due: 'bg-red-100 text-red-800',
      canceled: 'bg-gray-100 text-gray-800',
      incomplete: 'bg-yellow-100 text-yellow-800',
      trialing: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status ? status.toUpperCase() : 'FREE'}
      </span>
    );
  };

  if (loading) {
    return <div className="p-8 text-center">Loading subscription details...</div>;
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Subscription</h1>

      {/* Current Subscription */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {host?.tier_name || 'Free Trial'}
            </h2>
            <p className="text-gray-600">
              {host?.coupon_id ? 'Activated with coupon code' : 'Your current plan'}
            </p>
          </div>
          {getStatusBadge(host?.subscription_status)}
        </div>

        {subscription && (
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Billing Cycle</p>
              <p className="font-semibold">{subscription.billing_cycle === 'year' ? 'Annual' : 'Monthly'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Period Ends</p>
              <p className="font-semibold">
                {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
              </p>
            </div>
            {subscription.cancel_at_period_end && (
              <div className="md:col-span-2">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-semibold">
                    ⚠️ Your subscription will cancel on {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                  </p>
                  <button
                    onClick={handleReactivate}
                    disabled={processing}
                    className="mt-2 text-blue-600 hover:text-blue-800 font-semibold underline"
                  >
                    Reactivate Subscription
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {host?.subscription_status === 'past_due' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 font-semibold mb-2">
              ⚠️ Payment Failed - Action Required
            </p>
            <p className="text-red-700 text-sm mb-3">
              We couldn't process your last payment. Please update your payment method to avoid service interruption.
            </p>
            <button
              onClick={handleUpdatePayment}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              Update Payment Method
            </button>
          </div>
        )}

        {host?.games_per_month && (
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-1">Games per Month</p>
            <p className="font-bold text-2xl text-teal-600">
              {host.games_per_month === -1 ? 'Unlimited' : host.games_per_month}
            </p>
          </div>
        )}

        {host?.stripe_subscription_id && !subscription?.cancel_at_period_end && (
          <div className="flex gap-3">
            <button
              onClick={() => setShowUpgrade(!showUpgrade)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              {showUpgrade ? 'Hide Plans' : 'Change Plan'}
            </button>
            <button
              onClick={handleUpdatePayment}
              disabled={processing}
              className="bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              Update Payment Method
            </button>
            <button
              onClick={() => handleCancel(false)}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition"
            >
              Cancel Subscription
            </button>
          </div>
        )}
      </div>

      {/* Upgrade/Downgrade Section */}
      {showUpgrade && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Change Your Plan</h2>

          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setSelectedCycle('month')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                selectedCycle === 'month' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedCycle('year')}
              className={`px-4 py-2 rounded-lg font-semibold ${
                selectedCycle === 'year' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              Annual (Save 17%)
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {tiers.map(tier => (
              <button
                key={tier.id}
                onClick={() => setSelectedTier(tier)}
                disabled={tier.id === host?.subscription_tier_id}
                className={`p-6 border-2 rounded-lg text-left transition ${
                  tier.id === host?.subscription_tier_id
                    ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                    : selectedTier?.id === tier.id
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-300 bg-white hover:border-teal-300'
                }`}
              >
                <div className="font-bold text-xl mb-2">{tier.name}</div>
                {tier.id === host?.subscription_tier_id && (
                  <div className="text-sm text-gray-600 mb-2">Current Plan</div>
                )}
                <div className="text-3xl font-bold text-teal-600 mb-2">
                  ${selectedCycle === 'year' ? tier.price_annual : tier.price_monthly}
                  <span className="text-sm text-gray-600">
                    /{selectedCycle === 'year' ? 'year' : 'month'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {tier.games_per_month === -1 ? 'Unlimited' : tier.games_per_month} games/month
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={handleUpgrade}
            disabled={processing || !selectedTier || selectedTier.id === host?.subscription_tier_id}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-lg transition disabled:opacity-50"
          >
            {processing ? 'Processing...' : 'Change Plan'}
          </button>
          <p className="text-sm text-gray-600 text-center mt-2">
            You'll be charged or credited the prorated difference immediately
          </p>
        </div>
      )}

      {/* Free/Coupon Account Info */}
      {!host?.stripe_subscription_id && host?.coupon_id && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-bold text-lg text-blue-900 mb-2">Free Account via Coupon</h3>
          <p className="text-blue-800">
            Your account was activated with a coupon code. If you'd like to upgrade to a paid plan for additional features, please contact support.
          </p>
        </div>
      )}
    </div>
  );
}
